import { ol_Feature } from "@omnia/exports/openlayers"
import { CompareLayersPopupArguments } from "../compare/types"
import { EditLayerCmd } from "../edit/editCmd"
import { FieldViewModel, createMap, Datalayer, DatalayersMap, defineComponent, EditTarget, ExplorerContext, getAllNodes, IDataLayer, IDatalayerContext, IDatalayerViewerFactory, ILayerDetailsContext, ILayerViewModel, IOmniaSettings, IOmniaShell, isAdmin, isArrayEmpty, LayerTypeId, LayerViewModel, LmNode, LmPath, LocaleString, map, partition, selectMany, showLayers, str } from "../omnia"
import { IDatalayerManagerArguments, IDatalayerMapDependencies, IDatalayersService } from "../types"
import { getNextExpandedState, getNextExpandedStateCss } from "./expand"
import { expandPath, findPathToParent, getMenuRoots, LmRoot } from "./explorer"
import { applyFilters } from "./filters"
import { updateKeys } from "./keys"
import { replaceLayers } from "./layers"
import { restoreExplorerState, restoreState, saveExplorerState, saveState } from "./state"
import { isFieldViewModel } from "./types"
import { getContextMenuArguments } from "./utils"
import { getAvailableLayers } from "@omnia/shared/layers/layers"

export const mainCssClass: string = "style2"

export class MenuCtrl extends omnia.shell.Component implements ExplorerContext {

    static create(): ng.IDirective {

        return defineComponent(MenuCtrl, "app/datalayers/menu/menu.html", {
            map: "="
        });

    }

    private _settings: IOmniaSettings

    public map: DatalayersMap
    private mapWatcher: Function

    private _layerContext: IDatalayerContext

    public readonly viewManager = this.dependencies.viewManager

    private _fieldLayers: LayerViewModel[]
    private _holdingLayers: LayerViewModel[]
    private _allLayers: LayerViewModel[]

    private _roots: LmRoot[]
    private _root: LmRoot

    public readonly trackActiveLayer = true
    public readonly trackActiveField = true

    public nodes: LmNode[]

    public activeLayer: ILayerViewModel
    public activePaths: LmPath[]
    public activeNodes: LmNode[]

    static $inject = ["$scope", "OmniaDatalayersService", "OmniaDatalayersDependencies", "OmniaShell"];

    constructor(
        $scope: ng.IScope,
        public readonly svc: IDatalayersService,
        private dependencies: IDatalayerMapDependencies,
        private shell: IOmniaShell,
        public readonly readOnlySession = shell.isReadOnly(p => p.mapLayers)) {

        super($scope)

        this._icon = "icon-datalayers"
        this._title = str.MapLayers
        this._pinnable = true
        this._filter = omnia.fields.createFilter(dependencies.fieldSvc)
        this._settings = this.shell.getSession().getSettings("maplayers")

    }

    $onInit() {

        this.setupCommands()

        this._layerContext = this.dependencies.createContext(() => (this._fieldLayers || []).map(l => l.mapLayer).filter(l => !!l))

        this.map.addTooltipProvider(tt => this.getContextMenu(tt))

        this.$scope.$on("omnia.fields.filter", () => {
            if (this.svc.ready) {
                this.applyFilters()
                this._fieldLayers.forEach(l => l.refreshVisibility())
            }
        })

        this.mapWatcher = this.map.watchSelection(f => this.synchronizeFromMapSelection(f))

        let started = false

        this.setupAutoRefresh(() => {
            const init = !started
            started = true
            this.refresh(init)
        }, true)

    }

    $onDestroy() {
        this.saveState()
        this.dispose()
    }


    private startInitAction() {

        const params = this.shell.getRouteParams()

        const { action, id } = params
        if (!action) return

        switch (params["action"]) {

            case "manage":
                this.manageLayers()
                break

            case "terramap":
                this.manageTerramap()
                break

            case "edit-layer":
                this.editLayerAndClose(id)
                break

            case "compare-layers":
                this.compareFieldLayers(Number(id))
                break

        }


    }

    refresh(init: boolean) {

        if (this.nodes) {
            this.saveState()
        }

        return this.shell.disableBlockUI(() => this.setupLayers().then(() => {

            this.setupDetailsBox()

            this.restoreState()

            if (init) {
                this.startInitAction()
            }

        }))

    }

    dispose() {

        if (this.mapWatcher) {
            this.mapWatcher()
            this.mapWatcher = undefined
        }


    }

    private setupCommands() {

        const admin = isAdmin(this.shell)

        this._commands = [
            {
                text: "Yield map colors" as LocaleString,
                icon: "color",
                execute: () => this.manageYieldScales()
            },
            {
                text: "Manage layers" as LocaleString,
                icon: "manage",
                execute: () => this.manageLayers(),
            },
            admin &&
            {
                text: "Manage Terramap layers" as LocaleString,
                icon: "fas fa-sliders-h",
                execute: () => this.manageTerramap(),
            },
        ].filter(c => !!c)

    }


    private setupLayers() {

        return this.svc.getHoldingLayers().then(layers => {

            const [hl, fl] = partition(layers, l => !l.FieldId && !l.CroppingId && !!l.HoldingId)

            const fields = this.svc.fields

            const getField = (l: IDataLayer) => {
                const id = l.FieldId
                return id ? fields.find(f => f.fieldId == id) : undefined
            }

            const createVms = (layers: IDataLayer[]) => {
                return layers
                    .map(l => new LayerViewModel(this._layerContext, this.map, l, getField(l)))
                    .sort((l1, l2) => l1.description.localeCompare(l2.description))
            }

            const current = this._allLayers

            this._fieldLayers = createVms(fl)
            this._holdingLayers = createVms(hl)
            const updated = this._allLayers = this._fieldLayers.concat(this._holdingLayers)

            return replaceLayers(updated, current).then(() => {

                this._roots = getMenuRoots()

            })

        })

    }

    private setupDetailsBox() {

        this.map.detailsBox = {
            component: "omnia-datalayers-details",
            isVisible: () => this.map && this.map.showDistributions,
            context: <ILayerDetailsContext>{
                map: this.map,
                layers: this._fieldLayers || [],
                getSelectedField: () => this.svc.selectedField
            }
        }

    }


    private setupExplorer(root: LmRoot) {

        if (this.nodes) {
            saveExplorerState({ ...this.root, nodes: this.nodes })
        }

        this._root = root
        this.activeLayer = null

        const nodesContext = this.getNodesContext()

        this.nodes = this._root.provider(nodesContext)

        restoreExplorerState({ ...root, nodes: this.nodes })

        this.applyFilters()

        this.synchronizeFromMapSelection(this.map.getSelectedFeatures())

        this.saveState()

    }

    getNodesContext() {

        const { svc } = this

        const layers = getAvailableLayers(svc, this._allLayers, l => l.datalayer)

        const set = new Set(layers.map(l => l.DataLayerId))

        const layersMap = createMap(
            this._allLayers.filter(l => set.has(l.datalayerId)),
            l => l.datalayerId
        )

        const getVm = (l: IDataLayer) => layersMap.get(l.DataLayerId)

        return {
            year: svc.yearId,
            fields: svc.fields,
            layers,
            layerTypes: svc.getLayerTypes(),
            layerProvider: (l: IDataLayer) => getVm(l)
        }

    }

    applyFilters() {
        applyFilters(this.nodes, null)
    }

    get mainCssClass() {
        return mainCssClass
    }

    get roots() {
        return this._roots
    }

    get root() {
        return this._root
    }

    set root(value) {
        this.setupExplorer(value)
    }

    getLayers(filter?: (l: LayerViewModel) => boolean) {
        const layers = [...this._allLayers]
        return filter ? layers.filter(filter) : layers
    }

    get activeField() {
        return this.svc.selectedField
    }

    set activeField(field) {
        const { svc } = this
        if (svc.selectedField != field) {
            svc.selectedField = field
        }
    }

    getField(fieldId: number) {
        return this.svc.fields.find(f => f.fieldId == fieldId)
    }

    getLayer(layerId: number) {
        return this._allLayers.find(l => l.datalayerId == layerId)
    }

    private saveState() {
        saveState(this.svc, this._settings, this._fieldLayers, { ...this.root, nodes: this.nodes })
    }

    private restoreState() {
        const { root } = restoreState(this.svc, this._settings, this._fieldLayers, this._roots)
        this.setupExplorer(root)
        this.updateKeys()
    }

    getActualRootNodes() {

        const nodes = this.nodes

        const vn = (nodes || []).filter(n => !n.filtered)

        if (vn && (vn.length == 1) && (vn[0].layerTypeGroup)) {
            return vn[0].getChildren()
        }

        return nodes

    }

    toggleExpanded() {

        const roots = this.getActualRootNodes()

        const s = getNextExpandedState(roots)
        s.action()

        this.activeNodes = this.activePaths = undefined

    }

    getNextExpandedStateCss() {
        const roots = this.getActualRootNodes()
        return getNextExpandedStateCss(roots)
    }

    resetView() {

        this._fieldLayers.forEach(l => l.visible = false)

        this.activePaths = null
        this.activeNodes = null
        this.activeLayer = null

        const nodes = getAllNodes(this.nodes)

        for (const n of nodes) {

            n.opacitySlider = false
            n.expanded = false

            if (n.layer) {
                n.layer.opacity = 100
            }

        }

        this.root.setDefaults(this.nodes)

        this.applyFilters()

        this.updateKeys()

        this.svc.selectedField = null

        this.map.viewDefaultExtent()

        this.saveState()

    }

    updateKeys(layers: LayerViewModel[] = []) {

        const visible = (this._fieldLayers ?? []).filter(l => l.visible)

        layers = layers.concat(visible)

        updateKeys(this.map, layers)

    }

    editLayer(target: EditTarget, create: boolean) {

        const { layer } = target

        if ((!layer) || layer.zonesLoaded) {
            return this.editLoadedLayer(target, create)
        }
        else if (layer) {
            return this.svc.loadLayerZones([layer.datalayer]).then(() => this.editLoadedLayer(target, create))
        }

    }

    /**
     * This is used only when the module is invoked from another one to edit a layer
     * Once the editor is closed, the browser is sent back to the previous page
     * using its "history" object
     */
    private editLayerAndClose(id: string) {

        const layerId = Number(id)

        if (!id) {
            return
        }

        const nodes = getAllNodes(this.nodes)
        const layer = nodes.find(n => n.layer?.datalayerId === layerId)

        if (layer) {
            this.editLayer(layer, false).then(() => {
                history.back()
            })
        }


    }

    private editLoadedLayer(target: EditTarget, create: boolean): PromiseLike<any> {

        const { layer, field, layerType } = target

        this.map.detailsBox = null

        this.saveState()

        if (field) {
            this.map.viewExtentForDataItem(field)
        }

        const cmd = new EditLayerCmd(this.componentContext, "")

        const layers = this._fieldLayers.concat(this._holdingLayers)

        const ctxt = cmd.editLayer({
            target: field,
            holding: field ? null : this.svc.holding,
            layer: (!create) && layer ? layer.datalayer : null,
            layerType,
            availableLayers: layers
        })

        if (ctxt && ctxt.result) {

            return ctxt.result
                .then((layer: IDataLayer) => {

                    this.ignoreNextRefresh()

                    return this.refresh(false).then(() => {

                        if (field) {
                            this.svc.selectedField = field
                        }

                        if (layer) {

                            const editedLayer = this._allLayers.find(l => l.datalayerId == layer.DataLayerId)

                            if (editedLayer) {
                                editedLayer.visible = true
                                this.setActiveLayer(editedLayer)
                            }
                        }

                    })

                })

        }



    }

    /** Temporary buffer for the "showLayers" method */
    private showLayersBuffer: Set<ILayerViewModel>

    /**
     * Loads (if required) and displays a list of layers
     *
     * @param layers Layers to display
     * @param buffer If true, the layers will be buffered temporarily before being displayed, which allows calling this method multiple times before they are loaded in one batch
     */
    public showLayers(layers: ILayerViewModel[], buffer: boolean) {

        if (!buffer) {
            showLayers(this._layerContext, layers)
            return
        }

        // If the buffer has not been intialized yet,
        // then it is, and a "requestAnimationFrame" callback
        // is registered to process all the buffered layers later
        if (!this.showLayersBuffer) {

            this.showLayersBuffer = new Set()

            window.requestAnimationFrame(() => {

                const layers = Array.from(this.showLayersBuffer)
                this.showLayersBuffer = undefined

                showLayers(this._layerContext, layers)

            })

        }

        // Add to the buffer
        for (const l of layers) {
            this.showLayersBuffer.add(l)
        }

    }

    public setViewer(type: LayerTypeId, viewer: IDatalayerViewerFactory) {

        const mgr = this.viewManager
        mgr.setCurrentViewer(type, viewer)

        this._fieldLayers.filter(l => l.typeId == type).forEach(l => l.setViewer(viewer))

        this.updateKeys()

    }

    public onLayerVisibilityChanged(layers: LayerViewModel[], visible: boolean) {

        layers = (visible ? layers : [])

        this.updateKeys(layers)

    }

    private synchronizeFromMapSelection(features: ol_Feature[]) {

        const feature = features.length >= 1 ? features[0] : null
        const field = feature ? feature.get("data") : undefined

        if (!isFieldViewModel(field)) {
            this.activeNodes = this.activePaths = undefined
            return
        }

        this.activeField = field

        const layer = this.map.getTopmostLayer(l => l.fieldId == field.fieldId)

        if (layer) {
            this.setActiveLayer(layer)
        }
        else {
            this.setActiveField(field)
        }

    }

    locateInExplorer(target: FieldViewModel | Datalayer) {

        if (target instanceof Datalayer) {
            this.setActiveLayer(target)
        }
        else if (isFieldViewModel(target)) {
            this.setActiveField(target)
        }

    }

    private setActiveField(field: { fieldId: number }) {

        const { fieldId: id } = field

        const paths = this.root.getPathsToField(this.nodes, id)

        if (paths) {

            this.activePaths = paths
            this.activeNodes = selectMany(paths)

            if ((this.activeField) && (this.activeField.fieldId == id)) {
                return
            }

            const query = `.field-${id}`

            const scrollOptions: ScrollIntoViewOptions = {
                behavior: "smooth",
                block: "center"
            }

            this.shell.timeout().then(() => this.scrollItemIntoView(query, scrollOptions))

        }

    }

    private setActiveLayer(layer: { datalayerId: number }) {

        const id = layer.datalayerId

        if ((this.activeLayer) && (this.activeLayer.datalayerId == id)) {
            return
        }

        this.activeLayer = this._fieldLayers.find(l => l.datalayerId == id)

        const query = `.layer-${id}`

        this.shell.timeout().then(() => {

            const scrollOptions: ScrollIntoViewOptions = {
                behavior: "smooth",
                block: "center"
            }

            if (!this.scrollItemIntoView(query, scrollOptions)) {

                const path = findPathToParent(this.nodes, c => c.layer && c.layer.datalayerId == id, true)

                if (path) {

                    expandPath(path)

                    this.shell.timeout().then(() => this.scrollItemIntoView(query, scrollOptions))

                }

            }

        })

        return true

    }

    private getContextMenu(tt: map.IMapTooltip) {

        if (!tt.contextMenu) {
            return false
        }

        const args = getContextMenuArguments(this, tt)

        if (!args) {
            return false
        }

        tt.context = args
        tt.template = "app/datalayers/menu/cxm/tmpl.html"

        return true

    }

    removeActiveNode(node: LmNode) {

        if (!this.activePaths) return

        this.activePaths = this.activePaths
            .map(p => {

                const i = p.indexOf(node)

                return (i < 0)
                    ? p
                    : p.slice(0, i)

            })
            .filter(p => !isArrayEmpty(p))

        this.activeNodes = selectMany(this.activePaths)

    }

    manageLayers() {

        const args: IDatalayerManagerArguments = {
            mapContext: this._layerContext,
            field: null,
        };

        this.componentContext.startComponent({
            component: "omnia-datalayers-manager",
            execArguments: args
        });

    }

    manageTerramap() {

        const args = {
            svc: this.svc,
            mapDependencies: this.dependencies
        }

        const context = this.componentContext.startComponent({
            component: "omnia-layers-terramap",
            execArguments: args
        })

        context.result.then(() => this.refresh(false))

    }

    manageYieldScales() {

        this.shell.showComponentAsDialog("omnia-maplayers-edit-yield-scales-popup", null, {
            backdrop: 'static',
            size: "lg"
        });

    }

    manageSatelliteImagery() {

        this.componentContext.startComponent({
            component: "omnia-satellite-main"
        })

    }

    compareFieldLayers(fieldId: number) {

        const args: CompareLayersPopupArguments = {
            fieldId,
            fieldLayers: this._fieldLayers
        }

        return this.shell.showComponentAsDialog("omnia-maplayer-compare-popup", args, { size: "full-screen" })

    }

}

